import { useState } from "react";
import { useTranslation } from "react-i18next";
import { validateEmail } from "common/validator";
import { updateDocumentPermitSettings } from "services/company.service";
import { Field, FIELD_INPUT_CLASS } from "../catalog-form-parts";
import { saveErrorKey } from "../save-error";
import {
  ChangeLink,
  EditPanel,
  SettingRow,
  SettingsCard,
  useInlineEdit,
  useSaveWithToast,
} from "../ui";

// This endpoint still refuses a non-Master with a 400 code instead of a 403.
const PERMIT_ERROR_KEYS = {
  COMPANY_USER_MASTER_NOT_FOUND: "settings_master_only",
};

interface DocumentPermitCardProps {
  companyId: string;
  documentPermitEmail?: string | null;
  /** Loads the company again; a save is done when that has finished. */
  onRefresh: () => Promise<unknown>;
}

/** Who gets direct access to the company folder in OneDrive. */
export default function DocumentPermitCard({
  companyId,
  documentPermitEmail,
  onRefresh,
}: DocumentPermitCardProps) {
  const { t } = useTranslation();
  const edit = useInlineEdit(documentPermitEmail ?? "");
  const { save, saving, error } = useSaveWithToast();
  // An invalid address is flagged after the first try to save, not while typing.
  const [triedToSave, setTriedToSave] = useState(false);

  const closeEditor = () => {
    edit.cancel();
    setTriedToSave(false);
  };

  const saveEmail = async () => {
    setTriedToSave(true);
    if (!validateEmail(edit.draft)) return;

    const saved = await save(
      () =>
        updateDocumentPermitSettings(
          { documentPermitEmail: edit.draft },
          companyId
        ).then(onRefresh),
      { errorMessage: (err) => t(saveErrorKey(err, PERMIT_ERROR_KEYS)) }
    );
    if (saved) closeEditor();
  };

  return (
    <SettingsCard>
      <SettingRow
        label={t("settings_storage_permit_label")}
        description={t("settings_storage_permit_hint")}
        value={documentPermitEmail || t("settings_none")}
        action={!edit.editing && <ChangeLink onClick={edit.start} />}
      >
        {edit.editing && (
          <EditPanel
            saving={saving}
            error={error}
            onSave={saveEmail}
            onCancel={closeEditor}
          >
            <Field htmlFor="document-permit-email" label={t("email")}>
              <input
                id="document-permit-email"
                type="email"
                className={`${FIELD_INPUT_CLASS} max-w-sm`}
                value={edit.draft}
                aria-invalid={triedToSave && !validateEmail(edit.draft)}
                onChange={(e) => edit.setDraft(e.target.value)}
              />
              {triedToSave && !validateEmail(edit.draft) && (
                <p className="mt-1 text-sm text-red-600">
                  {t("email_must_be_correct")}
                </p>
              )}
            </Field>
          </EditPanel>
        )}
      </SettingRow>
    </SettingsCard>
  );
}
